import {
  getAttendanceField,
  getPunchMillis,
  normalizePunchType,
} from "../explorer/attendanceFilterConfig";
import { detectDuplicates, detectMissingIn, detectMissingOut } from "./duplicateDetectionService";

/**
 * Attendance analytics computed over loaded/filtered explorer rows.
 * Reuses the attendance field resolvers and duplicate/missing detectors.
 */

const LATE_THRESHOLD_MIN = 9 * 60 + 15; // 09:15

function isSameDay(ms, ref) {
  const a = new Date(ms);
  const b = new Date(ref);
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function minutesOfDay(ms) {
  const d = new Date(ms);
  return d.getHours() * 60 + d.getMinutes();
}

function fmtClock(minutes) {
  if (minutes == null || Number.isNaN(minutes)) return "—";
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/**
 * Compute the attendance dashboard metrics for a set of rows.
 * `referenceDate` defaults to today; "today" metrics use that day.
 */
export function computeAttendanceAnalytics(rows = [], referenceDate = Date.now()) {
  const dup = detectDuplicates(rows);
  const missingIn = detectMissingIn(rows);
  const missingOut = detectMissingOut(rows);

  const branches = new Set();
  const machines = new Set();
  const employees = new Set();

  let inCount = 0;
  let outCount = 0;
  let todayPunches = 0;

  const firstInByEmpDay = new Map();

  rows.forEach((row) => {
    const emp = getAttendanceField(row, "employeeId");
    if (emp != null && emp !== "") employees.add(String(emp));
    const branch = getAttendanceField(row, "branch");
    if (branch != null && branch !== "") branches.add(String(branch));
    const device = getAttendanceField(row, "device");
    if (device != null && device !== "") machines.add(String(device));

    const type = normalizePunchType(getAttendanceField(row, "punchType"));
    const ms = getPunchMillis(row);

    if (ms != null && isSameDay(ms, referenceDate)) {
      todayPunches += 1;
      if (type === "IN") inCount += 1;
      else if (type === "OUT") outCount += 1;
    }

    if (type === "IN" && ms != null) {
      const key = `${String(emp ?? "")}|${new Date(ms).toDateString()}`;
      const existing = firstInByEmpDay.get(key);
      if (!existing || ms < existing) firstInByEmpDay.set(key, ms);
    }
  });

  let late = 0;
  firstInByEmpDay.forEach((ms) => {
    if (minutesOfDay(ms) > LATE_THRESHOLD_MIN) late += 1;
  });

  const presentEmployeesToday = new Set();
  rows.forEach((row) => {
    const ms = getPunchMillis(row);
    if (ms != null && isSameDay(ms, referenceDate)) {
      const emp = getAttendanceField(row, "employeeId");
      if (emp != null && emp !== "") presentEmployeesToday.add(String(emp));
    }
  });

  return {
    todayPunches,
    in: inCount,
    out: outCount,
    late,
    duplicate: dup.count,
    missingIn: missingIn.ids.size,
    missingOut: missingOut.ids.size,
    present: presentEmployeesToday.size,
    // Absent needs an employee roster that the loaded set cannot provide; we
    // report the total distinct employees in the loaded window instead.
    absent: null,
    employees: employees.size,
    branches: [...branches].sort(),
    machines: [...machines].sort(),
    duplicateGroups: dup.groups,
    missingInRecords: missingIn.records,
    missingOutRecords: missingOut.records,
    duplicateIds: dup.duplicateIds,
    missingInIds: missingIn.ids,
    missingOutIds: missingOut.ids,
  };
}

function employeeMatches(row, employeeId) {
  return String(getAttendanceField(row, "employeeId") ?? "") === String(employeeId);
}

/**
 * Timeline of a single employee's punches (optionally for one day).
 * @returns {Array<{ id, ms, time, type, device, branch }>}
 */
export function buildEmployeeTimeline(rows = [], employeeId, day = null) {
  const items = rows
    .filter((row) => employeeMatches(row, employeeId))
    .map((row) => {
      const ms = getPunchMillis(row);
      return {
        id: row.id,
        ms,
        time: ms != null ? new Date(ms).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—",
        date: ms != null ? new Date(ms).toLocaleDateString() : "—",
        type: normalizePunchType(getAttendanceField(row, "punchType")) || "—",
        device: getAttendanceField(row, "device") ?? "—",
        branch: getAttendanceField(row, "branch") ?? "—",
      };
    })
    .filter((p) => p.ms != null);

  const filtered = day ? items.filter((p) => new Date(p.ms).toDateString() === new Date(day).toDateString()) : items;
  return filtered.sort((a, b) => a.ms - b.ms);
}

/**
 * 30-day history summary for one employee.
 */
export function buildEmployeeHistory(rows = [], employeeId, windowDays = 30) {
  const cutoff = Date.now() - windowDays * 24 * 60 * 60 * 1000;
  const punches = rows
    .filter((row) => employeeMatches(row, employeeId))
    .map((row) => ({
      ms: getPunchMillis(row),
      type: normalizePunchType(getAttendanceField(row, "punchType")),
      device: getAttendanceField(row, "device"),
      branch: getAttendanceField(row, "branch"),
    }))
    .filter((p) => p.ms != null && p.ms >= cutoff)
    .sort((a, b) => a.ms - b.ms);

  const byDay = new Map();
  const machines = new Set();
  const branches = new Set();
  let lateCount = 0;

  punches.forEach((p) => {
    if (p.device != null && p.device !== "") machines.add(String(p.device));
    if (p.branch != null && p.branch !== "") branches.add(String(p.branch));
    const key = new Date(p.ms).toDateString();
    if (!byDay.has(key)) byDay.set(key, []);
    byDay.get(key).push(p);
  });

  const firstMinutes = [];
  const lastMinutes = [];
  byDay.forEach((list) => {
    list.sort((a, b) => a.ms - b.ms);
    const firstIn = list.find((p) => p.type === "IN") || list[0];
    const lastOut = [...list].reverse().find((p) => p.type === "OUT") || list[list.length - 1];
    if (firstIn) {
      const m = minutesOfDay(firstIn.ms);
      firstMinutes.push(m);
      if (m > LATE_THRESHOLD_MIN) lateCount += 1;
    }
    if (lastOut) lastMinutes.push(minutesOfDay(lastOut.ms));
  });

  const avg = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null);

  return {
    employeeId,
    windowDays,
    totalPunches: punches.length,
    activeDays: byDay.size,
    lateCount,
    // Absent count requires expected working days/roster; reported as unknown.
    absentCount: null,
    averageFirstPunch: fmtClock(avg(firstMinutes)),
    averageLastPunch: fmtClock(avg(lastMinutes)),
    machinesUsed: [...machines].sort(),
    branchesUsed: [...branches].sort(),
  };
}
